import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  CardProgram,
  CreateCardProgramPayload,
  KobbleClient,
  KobbleClientsResult,
  KobbleHealthData,
  ManualCreditPayload,
  ManualCreditResult,
  NotificationCampaign,
  NotificationPreviewPayload,
  NotificationPreviewResult,
  PaginatedData,
  PointsConfig,
  RentPayment,
  SendNotificationPayload,
  SendNotificationResult,
  SignInData,
  SyncMerchantPayload,
  SyncTransactionsPayload,
  SyncTransactionsResult,
  Transaction,
  TransactionListFilters,
  UpdateKycStatusPayload,
  UpdatePointsConfigPayload,
  NotificationSettings,
  UpdateNotificationSettingsPayload,
  UserRow,
  UserLoginUnlockResult,
  VerifyOtpData,
  AdminDevicePayload,
  AdminDeviceRegistration,
  SubscriptionPlan,
  SubscriptionPlanUpdate,
  UserSubscription,
  GrantSubscriptionPayload,
  CancelSubscriptionPayload,
  UserPointsListResult,
  PointTransactionRow,
  AdjustPointsPayload,
  AdjustPointsResult,
  EmailUserStatementPayload,
  EmailUserStatementResult,
  TilloBrand,
  TilloBrandsListResult,
  TilloBrandsSyncResult,
  WebhookLogDetail,
  WebhookLogListFilters,
  WebhookLogListResult,
  ShoppingPartnerCategory,
  ShoppingPartnerCategoryListResult,
  ShoppingPartner,
  ShoppingPartnerListResult,
  ShoppingPartnerPayload,
  ShoppingPartnerImageUploadResult,
  ShoppingPartnerRewardAggregator,
  ShoppingPartnerRewardUpload,
  ShoppingPartnerRewardUploadListResult,
  ShoppingPartnerRewardUploadDetail,
  MediaLibraryItem,
  MediaLibraryListResult,
  Agency,
  AgencyListResult,
  AgencyPayload,
  SupportTicket,
  SupportTicketListFilters,
  SupportTicketListResult,
  SupportTicketUpdatePayload,
} from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiBaseUrl;

  signIn(email: string, password: string, device?: AdminDevicePayload): Observable<SignInData> {
    return this.post<SignInData>('/api/v1/admin/auth/signIn', {
      email,
      password,
      ...(device || {}),
    });
  }

  verifyOtp(email: string, otp: string, device?: AdminDevicePayload): Observable<VerifyOtpData> {
    return this.post<VerifyOtpData>('/api/v1/admin/auth/verifyOTP', {
      email,
      otp,
      ...(device || {}),
    });
  }

  registerAdminDevice(payload: AdminDevicePayload): Observable<AdminDeviceRegistration> {
    return this.post<AdminDeviceRegistration>('/api/v1/admin/register/device', payload);
  }

  getUsers(page = 1, limit = 20): Observable<PaginatedData<UserRow>> {
    return this.get<PaginatedData<UserRow>>('/api/v1/admin/view/users', { page, limit });
  }

  getUserById(id: number | string): Observable<UserRow> {
    return this.get<UserRow>(`/api/v1/admin/view/user/${id}`);
  }

  unlockUserLogin(id: number | string): Observable<UserLoginUnlockResult> {
    return this.post<UserLoginUnlockResult>(`/api/v1/admin/users/${id}/unlock-login`, {});
  }

  updateKycStatus(payload: UpdateKycStatusPayload): Observable<UserRow> {
    return this.post<UserRow>('/api/v1/admin/update/kyc/status', payload);
  }

  getRentPayments(page = 1, limit = 20): Observable<PaginatedData<RentPayment>> {
    return this.get<PaginatedData<RentPayment>>('/api/v1/admin/view/rent/payments', { page, limit });
  }

  getTransactions(
    pageOrFilters: number | TransactionListFilters = 1,
    limit = 20
  ): Observable<PaginatedData<Transaction>> {
    const filters: TransactionListFilters =
      typeof pageOrFilters === 'number'
        ? { page: pageOrFilters, limit }
        : { page: 1, limit: 20, ...pageOrFilters };

    const query: Record<string, string | number> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query[key] = value as string | number;
    });

    return this.get<PaginatedData<Transaction>>('/api/v1/admin/view/transactions', query);
  }

  getTransactionById(id: number | string): Observable<Transaction> {
    return this.get<Transaction>(`/api/v1/admin/view/transaction/${id}`);
  }

  health(): Observable<unknown> {
    return this.get<unknown>('/api/v1/health');
  }

  getKobbleHealth(): Observable<KobbleHealthData> {
    return this.get<KobbleHealthData>('/api/v1/admin/view/kobble/health');
  }

  addManualCredit(payload: ManualCreditPayload): Observable<ManualCreditResult> {
    return this.post<ManualCreditResult>('/api/v1/admin/add/manual-credit', payload);
  }

  getCardPrograms(): Observable<CardProgram[]> {
    return this.get<CardProgram[]>('/api/v1/card-programs/admin/view');
  }

  createCardProgram(payload: CreateCardProgramPayload): Observable<CardProgram> {
    return this.post<CardProgram>('/api/v1/card-programs/admin/add', payload);
  }

  syncCardPrograms(): Observable<CardProgram[]> {
    return this.post<CardProgram[]>('/api/v1/card-programs/admin/sync', {});
  }

  setDefaultCardProgram(id: number | string): Observable<CardProgram> {
    return this.post<CardProgram>(`/api/v1/card-programs/admin/set-default/${id}`, {});
  }

  previewNotificationAudience(
    payload: NotificationPreviewPayload
  ): Observable<NotificationPreviewResult> {
    return this.post<NotificationPreviewResult>('/api/v1/admin/notifications/preview', payload);
  }

  sendNotificationCampaign(payload: SendNotificationPayload): Observable<SendNotificationResult> {
    return this.post<SendNotificationResult>('/api/v1/admin/notifications/send', payload);
  }

  getNotificationCampaigns(
    page = 1,
    limit = 20
  ): Observable<PaginatedData<NotificationCampaign>> {
    return this.get<PaginatedData<NotificationCampaign>>(
      '/api/v1/admin/notifications/campaigns',
      { page, limit }
    );
  }

  getNotificationCampaign(id: number | string): Observable<NotificationCampaign> {
    return this.get<NotificationCampaign>(`/api/v1/admin/notifications/campaigns/${id}`);
  }

  getPointsConfig(): Observable<PointsConfig> {
    return this.get<PointsConfig>('/api/v1/admin/view/points-config');
  }

  updatePointsConfig(payload: UpdatePointsConfigPayload): Observable<PointsConfig> {
    return this.put<PointsConfig>('/api/v1/admin/edit/points-config', payload);
  }

  getNotificationSettings(): Observable<NotificationSettings> {
    return this.get<NotificationSettings>('/api/v1/admin/view/notification-settings');
  }

  updateNotificationSettings(
    payload: UpdateNotificationSettingsPayload
  ): Observable<NotificationSettings> {
    return this.put<NotificationSettings>('/api/v1/admin/edit/notification-settings', payload);
  }

  getKobbleClients(): Observable<KobbleClientsResult | KobbleClient[]> {
    return this.get<KobbleClientsResult | KobbleClient[]>('/api/v1/admin/view/kobble/clients');
  }

  syncMerchantWallet(payload: SyncMerchantPayload = {}): Observable<PointsConfig> {
    return this.post<PointsConfig>('/api/v1/admin/sync/merchant-wallet', payload);
  }

  syncTransactions(payload: SyncTransactionsPayload = {}): Observable<SyncTransactionsResult> {
    return this.post<SyncTransactionsResult>('/api/v1/admin/sync/transactions', payload);
  }

  getSubscriptionPlans(): Observable<SubscriptionPlan[]> {
    return this.get<SubscriptionPlan[]>('/api/v1/admin/view/subscription-plans');
  }

  getSubscriptionPlan(id: number | string): Observable<SubscriptionPlan> {
    return this.get<SubscriptionPlan>(`/api/v1/admin/view/subscription-plan/${id}`);
  }

  updateSubscriptionPlan(
    id: number | string,
    payload: SubscriptionPlanUpdate
  ): Observable<SubscriptionPlan> {
    return this.put<SubscriptionPlan>(`/api/v1/admin/edit/subscription-plan/${id}`, payload);
  }

  getSubscriptions(
    page = 1,
    limit = 20,
    filters: { user_id?: number | string; status?: string; plan_key?: string } = {}
  ): Observable<PaginatedData<UserSubscription>> {
    const query: Record<string, string | number> = { page, limit };
    if (filters.user_id != null && filters.user_id !== '') query['user_id'] = filters.user_id;
    if (filters.status) query['status'] = filters.status;
    if (filters.plan_key) query['plan_key'] = filters.plan_key;
    return this.get<PaginatedData<UserSubscription>>('/api/v1/admin/view/subscriptions', query);
  }

  grantSubscription(payload: GrantSubscriptionPayload): Observable<unknown> {
    return this.post('/api/v1/admin/add/subscription/grant', payload);
  }

  cancelSubscription(payload: CancelSubscriptionPayload): Observable<unknown> {
    return this.post('/api/v1/admin/edit/subscription/cancel', payload);
  }

  deleteSubscription(subscriptionId: number): Observable<unknown> {
    return this.post('/api/v1/admin/edit/subscription/delete', {
      subscription_id: subscriptionId,
    });
  }

  getUserPoints(
    page = 1,
    limit = 20,
    q = ''
  ): Observable<UserPointsListResult> {
    const query: Record<string, string | number> = { page, limit };
    if (q.trim()) query['q'] = q.trim();
    return this.get<UserPointsListResult>('/api/v1/admin/view/points/users', query);
  }

  getPointsHistory(
    page = 1,
    limit = 20,
    filters: { user_id?: number | string; type?: string } = {}
  ): Observable<PaginatedData<PointTransactionRow>> {
    const query: Record<string, string | number> = { page, limit };
    if (filters.user_id != null && filters.user_id !== '') query['user_id'] = filters.user_id;
    if (filters.type) query['type'] = filters.type;
    return this.get<PaginatedData<PointTransactionRow>>(
      '/api/v1/admin/view/points/history',
      query
    );
  }

  adjustUserPoints(payload: AdjustPointsPayload): Observable<AdjustPointsResult> {
    return this.post<AdjustPointsResult>('/api/v1/admin/add/points/adjust', payload);
  }

  emailUserTransactionStatement(
    payload: EmailUserStatementPayload
  ): Observable<EmailUserStatementResult> {
    return this.post<EmailUserStatementResult>(
      '/api/v1/admin/send/user/transaction-statement',
      payload
    );
  }

  getTilloBrands(
    page = 1,
    perPage = 20,
    brandName = ''
  ): Observable<TilloBrandsListResult> {
    const query: Record<string, string | number> = { page, per_page: perPage };
    if (brandName.trim()) query['brand_name'] = brandName.trim();
    return this.get<TilloBrandsListResult>('/api/v1/admin/view/tillo_brands', query);
  }

  updateTilloBrand(
    id: number | string,
    payload: { points_per_dollar: number }
  ): Observable<TilloBrand> {
    return this.put<TilloBrand>(`/api/v1/admin/edit/tillo_brand/${id}`, payload);
  }

  syncTilloBrands(): Observable<TilloBrandsSyncResult> {
    return this.get<TilloBrandsSyncResult>('/api/v1/admin/sync/brands');
  }

  getShoppingPartnerCategories(
    page = 1,
    limit = 50,
    search = ''
  ): Observable<ShoppingPartnerCategoryListResult> {
    const query: Record<string, string | number> = { page, limit };
    if (search.trim()) query['search'] = search.trim();
    return this.get<ShoppingPartnerCategoryListResult>(
      '/api/v1/admin/view/shopping-partner-categories',
      query
    );
  }

  getShoppingPartnerCategory(id: number | string): Observable<ShoppingPartnerCategory> {
    return this.get<ShoppingPartnerCategory>(
      `/api/v1/admin/view/shopping-partner-category/${id}`
    );
  }

  createShoppingPartnerCategory(payload: Partial<ShoppingPartnerCategory>): Observable<ShoppingPartnerCategory> {
    return this.post<ShoppingPartnerCategory>(
      '/api/v1/admin/add/shopping-partner-category',
      payload
    );
  }

  updateShoppingPartnerCategory(
    id: number | string,
    payload: Partial<ShoppingPartnerCategory>
  ): Observable<ShoppingPartnerCategory> {
    return this.put<ShoppingPartnerCategory>(
      `/api/v1/admin/edit/shopping-partner-category/${id}`,
      payload
    );
  }

  deleteShoppingPartnerCategory(id: number | string): Observable<{ id: number }> {
    return this.http
      .delete<ApiResponse<{ id: number }>>(
        `${this.baseUrl}/api/v1/admin/edit/shopping-partner-category/${id}/delete`
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  getShoppingPartners(
    page = 1,
    limit = 20,
    filters: Record<string, string | number | boolean> = {}
  ): Observable<ShoppingPartnerListResult> {
    const query: Record<string, string | number> = { page, limit };
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query[key] = typeof value === 'boolean' ? (value ? 1 : 0) : value;
    });
    return this.get<ShoppingPartnerListResult>('/api/v1/admin/view/shopping-partners', query);
  }

  getShoppingPartner(id: number | string): Observable<ShoppingPartner> {
    return this.get<ShoppingPartner>(`/api/v1/admin/view/shopping-partner/${id}`);
  }

  createShoppingPartner(payload: ShoppingPartnerPayload): Observable<ShoppingPartner> {
    return this.post<ShoppingPartner>('/api/v1/admin/add/shopping-partner', payload);
  }

  updateShoppingPartner(
    id: number | string,
    payload: Partial<ShoppingPartnerPayload>
  ): Observable<ShoppingPartner> {
    return this.put<ShoppingPartner>(`/api/v1/admin/edit/shopping-partner/${id}`, payload);
  }

  deleteShoppingPartner(id: number | string): Observable<{ id: number }> {
    return this.http
      .delete<ApiResponse<{ id: number }>>(
        `${this.baseUrl}/api/v1/admin/edit/shopping-partner/${id}/delete`
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  uploadShoppingPartnerImage(
    file: File,
    meta?: { name?: string; description?: string }
  ): Observable<ShoppingPartnerImageUploadResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.name) formData.append('name', meta.name);
    if (meta?.description) formData.append('description', meta.description);
    return this.http
      .post<ApiResponse<ShoppingPartnerImageUploadResult>>(
        `${this.baseUrl}/api/v1/admin/add/shopping-partner-image`,
        formData
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  getMediaImages(page = 1, limit = 20, search = ''): Observable<MediaLibraryListResult> {
    const query: Record<string, string | number> = { page, limit };
    if (search.trim()) query['search'] = search.trim();
    return this.get<MediaLibraryListResult>('/api/v1/admin/view/media-images', query);
  }

  getMediaImage(id: number | string): Observable<MediaLibraryItem> {
    return this.get<MediaLibraryItem>(`/api/v1/admin/view/media-image/${id}`);
  }

  uploadMediaImage(
    file: File,
    meta?: { name?: string; description?: string }
  ): Observable<MediaLibraryItem> {
    const formData = new FormData();
    formData.append('file', file);
    if (meta?.name) formData.append('name', meta.name);
    if (meta?.description) formData.append('description', meta.description);
    return this.http
      .post<ApiResponse<MediaLibraryItem>>(`${this.baseUrl}/api/v1/admin/add/media-image`, formData)
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  updateMediaImage(
    id: number | string,
    payload: { name?: string; description?: string | null }
  ): Observable<MediaLibraryItem> {
    return this.put<MediaLibraryItem>(`/api/v1/admin/edit/media-image/${id}`, payload);
  }

  deleteMediaImage(id: number | string): Observable<{ id: number }> {
    return this.http
      .delete<ApiResponse<{ id: number }>>(
        `${this.baseUrl}/api/v1/admin/edit/media-image/${id}/delete`
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  getAgencies(page = 1, limit = 20, search = ''): Observable<AgencyListResult> {
    const query: Record<string, string | number> = { page, limit };
    if (search.trim()) query['search'] = search.trim();
    return this.get<AgencyListResult>('/api/v1/admin/view/agencies', query);
  }

  getAgency(id: number | string): Observable<Agency> {
    return this.get<Agency>(`/api/v1/admin/view/agency/${id}`);
  }

  createAgency(payload: AgencyPayload): Observable<Agency> {
    return this.post<Agency>('/api/v1/admin/add/agency', payload);
  }

  updateAgency(id: number | string, payload: Partial<AgencyPayload>): Observable<Agency> {
    return this.put<Agency>(`/api/v1/admin/edit/agency/${id}`, payload);
  }

  deleteAgency(id: number | string): Observable<{ id: number }> {
    return this.http
      .delete<ApiResponse<{ id: number }>>(
        `${this.baseUrl}/api/v1/admin/edit/agency/${id}/delete`
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  /** Create/update agency with optional image file (multipart field `image`). */
  saveAgencyMultipart(
    payload: AgencyPayload,
    image: File | null,
    id?: number | null
  ): Observable<Agency> {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined) return;
      if (value === null) {
        formData.append(key, '');
        return;
      }
      if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
        return;
      }
      formData.append(key, String(value));
    });
    if (image) formData.append('image', image);

    const url = id
      ? `${this.baseUrl}/api/v1/admin/edit/agency/${id}`
      : `${this.baseUrl}/api/v1/admin/add/agency`;
    const req$ = id
      ? this.http.put<ApiResponse<Agency>>(url, formData)
      : this.http.post<ApiResponse<Agency>>(url, formData);
    return req$.pipe(
      map((res) => this.unwrap(res)),
      catchError((err) => this.handleError(err))
    );
  }

  getShoppingPartnerRewardAggregators(): Observable<{ aggregators: ShoppingPartnerRewardAggregator[] }> {
    return this.get<{ aggregators: ShoppingPartnerRewardAggregator[] }>(
      '/api/v1/admin/view/shopping-partner-reward-aggregators'
    );
  }

  getShoppingPartnerRewardUploads(
    page = 1,
    limit = 20,
    filters: { aggregator?: string; status?: string } = {}
  ): Observable<ShoppingPartnerRewardUploadListResult> {
    const query: Record<string, string | number> = { page, limit };
    if (filters.aggregator) query['aggregator'] = filters.aggregator;
    if (filters.status) query['status'] = filters.status;
    return this.get<ShoppingPartnerRewardUploadListResult>(
      '/api/v1/admin/view/shopping-partner-reward-uploads',
      query
    );
  }

  getShoppingPartnerRewardUpload(
    id: number | string,
    page = 1,
    limit = 50,
    status?: string
  ): Observable<ShoppingPartnerRewardUploadDetail> {
    const query: Record<string, string | number> = { page, limit };
    if (status) query['status'] = status;
    return this.get<ShoppingPartnerRewardUploadDetail>(
      `/api/v1/admin/view/shopping-partner-reward-upload/${id}`,
      query
    );
  }

  uploadShoppingPartnerRewardCsv(
    aggregator: string,
    file: File
  ): Observable<ShoppingPartnerRewardUpload> {
    const formData = new FormData();
    formData.append('aggregator', aggregator);
    formData.append('csv_file', file);
    return this.http
      .post<ApiResponse<ShoppingPartnerRewardUpload>>(
        `${this.baseUrl}/api/v1/admin/add/shopping-partner-reward-upload`,
        formData
      )
      .pipe(
        map((res) => this.unwrap(res)),
        catchError((err) => this.handleError(err))
      );
  }

  /** Create/update partner with optional logo/banner/card_image files (multipart). */
  saveShoppingPartnerMultipart(
    payload: ShoppingPartnerPayload,
    files: { logo?: File | null; banner?: File | null; card_image?: File | null },
    id?: number | null
  ): Observable<ShoppingPartner> {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined) return;
      if (key === 'points' || key === 'metadata') {
        formData.append(key, JSON.stringify(value ?? (key === 'points' ? [] : null)));
        return;
      }
      if (value === null) {
        formData.append(key, '');
        return;
      }
      if (typeof value === 'boolean') {
        formData.append(key, value ? 'true' : 'false');
        return;
      }
      formData.append(key, String(value));
    });
    if (files.logo) formData.append('logo', files.logo);
    if (files.banner) formData.append('banner', files.banner);
    if (files.card_image) formData.append('card_image', files.card_image);

    const url = id
      ? `${this.baseUrl}/api/v1/admin/edit/shopping-partner/${id}`
      : `${this.baseUrl}/api/v1/admin/add/shopping-partner`;
    const req$ = id
      ? this.http.put<ApiResponse<ShoppingPartner>>(url, formData)
      : this.http.post<ApiResponse<ShoppingPartner>>(url, formData);
    return req$.pipe(
      map((res) => this.unwrap(res)),
      catchError((err) => this.handleError(err))
    );
  }

  getSupportTickets(filters: SupportTicketListFilters = {}): Observable<SupportTicketListResult> {
    const query: Record<string, string | number> = {
      page: filters.page || 1,
      limit: filters.limit || 20,
    };
    if (filters.status) query['status'] = filters.status;
    if (filters.topic) query['topic'] = filters.topic;
    if (filters.user_id) query['user_id'] = filters.user_id;
    if (filters.search?.trim()) query['search'] = filters.search.trim();
    return this.get<SupportTicketListResult>('/api/v1/admin/view/support-tickets', query);
  }

  getSupportTicket(id: number | string): Observable<SupportTicket> {
    return this.get<SupportTicket>(`/api/v1/admin/view/support-ticket/${id}`);
  }

  updateSupportTicket(
    id: number | string,
    payload: SupportTicketUpdatePayload
  ): Observable<SupportTicket> {
    return this.put<SupportTicket>(`/api/v1/admin/edit/support-ticket/${id}`, payload);
  }

  getWebhookLogs(filters: WebhookLogListFilters = {}): Observable<WebhookLogListResult> {
    const query: Record<string, string | number> = {
      page: filters.page || 1,
      limit: filters.limit || 20,
      provider: filters.provider || 'all',
    };
    if (filters.status) query['status'] = filters.status;
    if (filters.event_type) query['event_type'] = filters.event_type;
    if (filters.q) query['q'] = filters.q;
    return this.get<WebhookLogListResult>('/api/v1/admin/view/webhook-logs', query);
  }

  getWebhookLogById(
    id: number | string,
    provider: string,
    source?: string | null
  ): Observable<WebhookLogDetail> {
    const query: Record<string, string | number> = { provider };
    if (source) query['source'] = source;
    return this.get<WebhookLogDetail>(`/api/v1/admin/view/webhook-log/${id}`, query);
  }

  private get<T>(path: string, query?: Record<string, string | number>): Observable<T> {
    let params = new HttpParams();
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        params = params.set(key, String(value));
      });
    }
    return this.http.get<ApiResponse<T>>(`${this.baseUrl}${path}`, { params }).pipe(
      map((res) => this.unwrap(res)),
      catchError((err) => this.handleError(err))
    );
  }

  private post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<ApiResponse<T>>(`${this.baseUrl}${path}`, body).pipe(
      map((res) => this.unwrap(res)),
      catchError((err) => this.handleError(err))
    );
  }

  private put<T>(path: string, body: unknown): Observable<T> {
    return this.http.put<ApiResponse<T>>(`${this.baseUrl}${path}`, body).pipe(
      map((res) => this.unwrap(res)),
      catchError((err) => this.handleError(err))
    );
  }

  private unwrap<T>(res: ApiResponse<T>): T {
    if (res.status !== 'success' && res.status !== 'warning') {
      throw {
        statusCode: res.statusCode,
        message: res.message || 'Request failed',
        error: res.error,
      };
    }
    return res.data;
  }

  private handleError(err: unknown) {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as ApiResponse | undefined;
      return throwError(() => ({
        statusCode: body?.statusCode ?? err.status,
        message: body?.message || err.message || 'Network error',
        error: body?.error ?? err.error,
      }));
    }
    return throwError(() => err);
  }
}
